/**
 * Formula helpers for CBP calculated columns.
 *
 * A calculated column's formula references other columns by id in square brackets — `[columnId]` —
 * the same convention the TPA/calculation formula editors use (see ATPTextAreaAutoComplete, which
 * emits `[value]` on `@`-insert). Aggregates wrap a reference, e.g. `SUM([headcount])`.
 */
import { ExpressionPart, ExpressionPartTypes } from 'src/models/calculations/CalculationDetailsResult';

const REFERENCE = /\[([^\]]+)\]/g;

/**
 * Extract the distinct `[columnId]` references a formula reads — the columns it depends on, including
 * those inside aggregates like `SUM([x])`. Used for the persisted `dependsOn` (advisory: the server
 * re-derives dependencies from the formula text) and for the builder's own self-reference /
 * unknown-reference validation. On the wire `dependsOn` is simply every column the formula reads;
 * there is no separate aggregate-dependency field, so none is computed here.
 */
export function extractFormulaReferences(formula = ''): string[] {
    const refs = new Set<string>();
    const re = new RegExp(REFERENCE);
    let match: RegExpExecArray | null;
    while ((match = re.exec(formula)) !== null) refs.add(match[1]);
    return Array.from(refs);
}

/**
 * Map a `[columnId]` formula to ExpressionParts for the shared `renderExpressionParts` renderer —
 * references become STEP parts (rendered by the existing `.calcExpression-S` outlined pill style),
 * everything else is an OPERATOR part (plain text).
 */
export function toExpressionParts(formula = '', nameById: Record<string, string>): ExpressionPart[] {
    const parts: ExpressionPart[] = [];
    const re = new RegExp(REFERENCE);
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(formula)) !== null) {
        if (match.index > last) parts.push({ type: ExpressionPartTypes.OPERATOR, value: formula.slice(last, match.index) });
        const id = match[1];
        parts.push({ type: ExpressionPartTypes.STEP, value: id, displayValue: nameById[id] ?? id });
        last = match.index + match[0].length;
    }
    if (last < formula.length) parts.push({ type: ExpressionPartTypes.OPERATOR, value: formula.slice(last) });
    return parts;
}
